import { z } from 'zod';
import {
  ASPECT_RATIOS,
  MANUAL_VIDEO_ASPECTS,
  PHOTO_MODES,
  PROMPT_MAX_LENGTH,
  SONG_PROMPT_MAX,
  SONG_STYLE_MAX,
} from './generations.js';

/** Профиль текущего юзера — ответ `GET /v1/me` и `POST /v1/auth`. */
export const meSchema = z.object({
  id: z.number().int(),
  /** Telegram id строкой: он может не влезать в безопасный int JS. */
  telegramId: z.string(),
  firstName: z.string(),
  username: z.string().nullable(),
  languageCode: z.string().nullable(),
  refCode: z.string(),
  /** Согласие 18+ и с правилами (экран онбординга). */
  hasConsent: z.boolean(),
  /** Бесплатная первая генерация ещё не потрачена. */
  freeGenerationAvailable: z.boolean(),
  /** Баланс в кредитах (1 кредит = 1 ₽). Считается из журнала — с недели 2. */
  balance: z.number().int(),
  /** Настройки (уведомления в боте). */
  settings: z.lazy(() => userSettingsSchema).optional(),
});
export type Me = z.infer<typeof meSchema>;

export const userSettingsSchema = z.object({
  notify: z.object({ ready: z.boolean(), trends: z.boolean(), promo: z.boolean() }),
});
export type UserSettings = z.infer<typeof userSettingsSchema>;
export const DEFAULT_USER_SETTINGS: UserSettings = { notify: { ready: true, trends: true, promo: true } };
export const updateSettingsSchema = z.object({
  notify: z.object({ ready: z.boolean(), trends: z.boolean(), promo: z.boolean() }).partial(),
});

export const consentRequestSchema = z.object({
  adult: z.literal(true),
  rules: z.literal(true),
});
export type ConsentRequest = z.infer<typeof consentRequestSchema>;

export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

// ---------- Оплата ----------

export const PAYMENT_METHODS = ['card_sbp', 'usdt'] as const;
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const PAYMENT_STATUSES = ['created', 'pending', 'paid', 'canceled', 'expired', 'refunded', 'chargeback'] as const;
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

/** Итоговые статусы, после которых мини-апп перестаёт ждать. */
export const FINAL_FOR_CLIENT: readonly PaymentStatus[] = ['paid', 'canceled', 'expired', 'refunded', 'chargeback'];

export const createPaymentRequestSchema = z.object({
  packageId: z.string().min(1).max(32),
  method: paymentMethodSchema,
});
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>;

export const paymentSchema = z.object({
  id: z.string().uuid(),
  status: paymentStatusSchema,
  method: paymentMethodSchema,
  packageId: z.string(),
  amount: z.number().int(),
  bonus: z.number().int(),
  credits: z.number().int(),
  /** Куда отправить юзера платить. Для крипты — ссылка t.me на счёт в @CryptoBot. */
  payUrl: z.string().nullable(),
  test: z.boolean(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  /** Текущий баланс юзера — для экрана «Готово»: «теперь N ₽». */
  balance: z.number().int(),
});
export type Payment = z.infer<typeof paymentSchema>;

export const paymentsConfigSchema = z.object({
  packages: z.array(
    z.object({
      id: z.string(),
      amount: z.number().int(),
      bonusPercent: z.number(),
      bonus: z.number().int(),
      credits: z.number().int(),
    }),
  ),
  defaultPackageId: z.string(),
  /** Способы, которые реально настроены на сервере. */
  methods: z.array(paymentMethodSchema),
});
export type PaymentsConfig = z.infer<typeof paymentsConfigSchema>;

// ---------- Генерации ----------


export const GENERATION_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export const generationStatusSchema = z.enum(GENERATION_STATUSES);
export type GenerationStatus = z.infer<typeof generationStatusSchema>;

export const createPhotoRequestSchema = z.object({
  kind: z.literal('photo'),
  mode: z.enum(PHOTO_MODES),
  prompt: z.string().trim().min(3).max(PROMPT_MAX_LENGTH),
  aspectRatio: z.enum(ASPECT_RATIOS),
  /** id загруженного референса (POST /v1/uploads). */
  referenceId: z.string().uuid().optional(),
  /** Взять двойника референсами («С моим двойником»). */
  useTwin: z.boolean().optional(),
});
export type CreatePhotoRequest = z.infer<typeof createPhotoRequestSchema>;

export const createVideoRequestSchema = z.object({
  kind: z.literal('video'),
  prompt: z.string().trim().min(3).max(PROMPT_MAX_LENGTH),
  aspectRatio: z.enum(MANUAL_VIDEO_ASPECTS),
  /** Фото, которое оживляем (первый кадр). */
  referenceId: z.string().uuid().optional(),
  useTwin: z.boolean().optional(),
});
export type CreateVideoRequest = z.infer<typeof createVideoRequestSchema>;

export const createSongRequestSchema = z.object({
  kind: z.literal('song'),
  /** О чём песня — Suno сам напишет текст. */
  prompt: z.string().trim().min(3).max(SONG_PROMPT_MAX),
  style: z.string().trim().max(SONG_STYLE_MAX).default(''),
  instrumental: z.boolean().default(false),
});
export type CreateSongRequest = z.input<typeof createSongRequestSchema>;

export const GENERATION_KINDS = ['photo', 'video', 'song', 'stickers'] as const;
export type GenerationKind = (typeof GENERATION_KINDS)[number];

export const generationResultSchema = z.object({
  url: z.string(),
  label: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  /** image | video | audio. */
  type: z.enum(['image', 'video', 'audio']).optional(),
  /** Обложка видео / песни. */
  coverUrl: z.string().nullable().optional(),
  /** Видео, собранное под площадки: 1:1, 16:9 (оригинал — url). */
  formats: z.array(z.object({ aspect: z.string(), url: z.string() })).optional(),
  title: z.string().nullable().optional(),
  durationSec: z.number().nullable().optional(),
  /** Текст песни. */
  lyrics: z.string().nullable().optional(),
});
export type GenerationResult = z.infer<typeof generationResultSchema>;

export const generationSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(GENERATION_KINDS),
  /** manual — ручной режим, trend — по тренду. */
  source: z.enum(['manual', 'trend']),
  trendId: z.string().uuid().nullable(),
  /** Для ручного режима фото — параметры «Ещё раз»; у трендов null. */
  mode: z.enum(PHOTO_MODES).nullable(),
  title: z.string(),
  prompt: z.string().nullable(),
  aspectRatio: z.string().nullable(),
  status: generationStatusSchema,
  /** Сколько стоила (0 — бесплатная). */
  price: z.number().int(),
  free: z.boolean(),
  /** Кадры: всего и готово. */
  frames: z.number().int(),
  framesDone: z.number().int(),
  /** Ссылки на результаты (подписанные, живут ~час). label — подпись кадра («Рыцарь»). */
  results: z.array(generationResultSchema),
  /** Проверка качества: сколько кадров проверено и сколько переделали за наш счёт. */
  qc: z.object({ checked: z.number().int(), regenerated: z.number().int() }).nullable().optional(),
  /** Взяли двойника. */
  twin: z.boolean().optional(),
  /** Стикерпак: ссылка t.me/addstickers/<name>; error — пак не собрался (стикеры всё равно в results). */
  stickers: z.object({ name: z.string().nullable(), url: z.string().nullable(), error: z.string().nullable() }).nullable().optional(),
  /** Дуо: с кем вместе; mine — генерацию запустил я (иначе меня позвали). */
  duo: z.object({ friendName: z.string(), mine: z.boolean() }).nullable().optional(),
  /** Деньги вернули (release) / бесплатную попытку вернули. */
  refunded: z.boolean(),
  /** Короткая причина провала для юзера. */
  error: z.string().nullable(),
  etaSec: z.number().int(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  balance: z.number().int(),
});
export type Generation = z.infer<typeof generationSchema>;

export const generationsConfigSchema = z.object({
  photo: z.object({
    modes: z.array(z.object({ id: z.enum(PHOTO_MODES), label: z.string(), hint: z.string(), price: z.number().int() })),
    aspectRatios: z.array(z.enum(ASPECT_RATIOS)),
    freeMode: z.enum(PHOTO_MODES),
  }),
  video: z
    .object({ price: z.number().int(), durationSec: z.number().int(), aspectRatios: z.array(z.enum(MANUAL_VIDEO_ASPECTS)), enabled: z.boolean() })
    .optional(),
  song: z.object({ price: z.number().int(), styles: z.array(z.string()), enabled: z.boolean() }).optional(),
  /** Включена ли загрузка референса (настроено S3). */
  uploads: z.boolean(),
});
export type GenerationsConfig = z.infer<typeof generationsConfigSchema>;

export const uploadSchema = z.object({ id: z.string().uuid(), url: z.string() });
export type Upload = z.infer<typeof uploadSchema>;
